import { useRef, useState } from "react";
import { useOrder } from "@utils/store/cart/useOrder";
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { useUser } from "@utils/store/user/useUser";
import { buildXContextHeader } from "@utils/api/headers";
import { appToast } from "@components/Toast";
import type { GasCylinder } from "@screen/Home/pages/GasShop/products";
import { useKiosk } from "../useKiosk";
import { createDraftWithGas, addProductItem, updateProductItem, draftFromItemRes } from "../api/kioskApi";

/** Kiosk 新草稿一律預設「立即配送」，且不帶入任何 slot（上一筆可能是預約，不沿用其時段）。
 *  freeze 防呆：此常數會被傳進 store，凍結可避免任何意外 mutation 汙染共用物件。 */
const IMMEDIATE_DELIVERY = Object.freeze({ type: "immediate" as const, date: null, slotId: null, slotSource: null, slotLabel: null });

interface AddToCartArgs {
  product: GasCylinder;
  usageLocation: string;
  floorInt: number | null;
  qty: number;
  note: string;
  lift: boolean;
  onAdded: () => void;
  onClose: () => void;
}

/**
 * 安裝精靈「加入購物車」的後端互動邏輯（與 UI 解耦）。
 * 處理：建立草稿、加品項、同規格同安裝位累加數量（GAS_ITEM_DUPLICATE_LINE）、
 * 草稿失效（404）自動以新草稿重建、cis 規格錯誤友善提示。
 */
export function useInstallAddToCart({ product, usageLocation, floorInt, qty, note, lift, onAdded, onClose }: AddToCartArgs) {
  const { draftOrderId, syncDraft, setOrderState, productList, clearProducts, setDeliveryTime } = useOrder();
  const { cusJwtToken } = useCusAuth();
  const { chooseIdentity } = useUser();
  const { entrySupplierId } = useKiosk();
  const xContext = buildXContextHeader(chooseIdentity) || "";
  const customerAddressId = chooseIdentity?.choose_address?.customer_address_id;
  // 無 CIS 的新 customer（onboarding 早建、還沒完成加入設定）沒有 primary_supplier，
  // 與 useKioskBootstrap 同法退回「入口瓦斯行」（QR/傳單解析出的 entrySupplierId），否則加購物車必失敗。
  const supplierId = chooseIdentity?.primary_supplier?.supplier_id || entrySupplierId || undefined;

  const [submitting, setSubmitting] = useState(false);
  /**
   * 送出中旗標用 ref 而非 state：setSubmitting 是非同步的，同一個 render 週期內的第二次點擊
   * 讀到的 submitting 仍是 false（按鈕的 disabled 同樣要等 re-render 才生效）→ 兩次都會送出。
   * 實測後果：手機連點一次新增，第一次成功、第二次撞後端重複防護回 409
   * GAS_ITEM_DUPLICATE_LINE，畫面跳「這筆瓦斯已在訂單中」，但使用者只按了一次。
   * ref 是同步的，能擋住同 tick 的重入。
   */
  const inFlightRef = useRef(false);

  const addToCart = async () => {
    if (inFlightRef.current || submitting || !usageLocation || floorInt === null) return;
    if (!customerAddressId || !supplierId) {
      // 不可靜默失敗：缺地址/供應商時明確提示，避免「按了沒反應」的無聲 no-op
      console.error("InstallSheet: missing address/supplier", { customerAddressId, supplierId });
      appToast.error(!customerAddressId ? "請先設定送達地址" : "找不到瓦斯行資訊，請重新整理");
      return;
    }
    inFlightRef.current = true;
    setSubmitting(true);
    const gasItem = {
      type: product.type,
      price_id: product.price_id,
      gas_id: product.gas_id,
      gas_type: product.gas_type,
      kilogram: product.kilogram,
      unit_price: product.unit_price,
      qty,
      usage_location: usageLocation,
      note: note.trim() || null,
      floor: floorInt,
      is_elevator: lift,
      customer_delivery_id: null, // BE 依內容（用途+樓層+電梯+備註）比對 upsert，免帶 id
    };
    // 同規格 + 同安裝位的品項，BE 不允許重複 line（GAS_ITEM_DUPLICATE_LINE），
    // 應改為「累加既有 line 的數量」。先在前端找出相符的 line。
    const sig = (priceId: any, usage: string, floor: number, l: boolean, n?: string | null) =>
      `${priceId}|${usage}|${floor}|${l ? 1 : 0}|${(n || "").trim()}`;
    const newSig = sig(product.price_id, usageLocation, floorInt, lift, note);
    const existing = productList.find((p) => p.line_id && sig(p.price_id, p.usage_location, p.floor, p.is_elevator, p.note) === newSig);

    const syncFromRes = (res: any) => {
      const draft = draftFromItemRes(res);
      if (draft) syncDraft(draft);
      else setOrderState("loading");
    };
    // 草稿已被刪除 / 過期 / 已送出 → BE 回 404 Draft not found
    const isDraftGone = (res: any) =>
      res?.error?.response?.status === 404 || /draft not found/i.test(res?.error?.response?.data?.message || "");

    // 用目前品項建立全新草稿（清掉失效的 draftOrderId 後重來）
    const createFresh = async () => {
      clearProducts();
      const draft = await createDraftWithGas({ supplierId, customerAddressId, gasItem, jwtToken: cusJwtToken || "", xContext });
      if (draft) {
        syncDraft(draft);
        setDeliveryTime(IMMEDIATE_DELIVERY); // 新草稿預設立即配送
      } else setOrderState("loading");
    };

    const bumpQty = async (lineId: string, baseQty: number) => {
      const res = await updateProductItem({
        type: "gas",
        draftId: draftOrderId!,
        lineId,
        body: { ...gasItem, qty: baseQty + qty },
        jwtToken: cusJwtToken || "",
        xContext,
      });
      if (res?.state === "success") {
        syncFromRes(res);
        return true;
      }
      if (isDraftGone(res)) {
        await createFresh();
        return true;
      }
      return false;
    };

    try {
      if (!draftOrderId) {
        const draft = await createDraftWithGas({ supplierId, customerAddressId, gasItem, jwtToken: cusJwtToken || "", xContext });
        if (draft) {
          syncDraft(draft);
          setDeliveryTime(IMMEDIATE_DELIVERY); // 第一筆品項：新草稿預設立即配送
        } else setOrderState("loading");
      } else if (existing) {
        // 已在購物車 → 直接累加數量
        if (!(await bumpQty(existing.line_id!, existing.qty))) {
          inFlightRef.current = false;
      setSubmitting(false);
          return;
        }
      } else {
        const res = await addProductItem({ type: "gas", draftId: draftOrderId, body: gasItem, jwtToken: cusJwtToken || "", xContext });
        if (res?.state === "success") {
          syncFromRes(res);
        } else if (isDraftGone(res)) {
          // 草稿失效 → 直接用新草稿重建
          await createFresh();
        } else {
          // 後援：BE 回 GAS_ITEM_DUPLICATE_LINE 時，用回傳的 line_id 累加數量
          const errData = (res?.error as any)?.response?.data;
          const dupLineId = errData?.code === "GAS_ITEM_DUPLICATE_LINE" ? errData.line_id : null;
          const dupLine = dupLineId ? productList.find((p) => p.line_id === dupLineId) : null;
          if (dupLineId && (await bumpQty(dupLineId, dupLine?.qty ?? 0))) {
            // recovered
          } else {
            inFlightRef.current = false;
            setSubmitting(false);
            return;
          }
        }
      }
      onAdded();
      onClose();
      inFlightRef.current = false; // 成功也要解除：sheet 關閉後元件可能沿用同一實例再開
    } catch (e) {
      console.error("addToCart error", e);
      // createDraftWithGas 走 throw；把後端訊息轉成友善提示
      const msg: string = (e as any)?.response?.data?.message || "";
      if (/cis/i.test(msg)) {
        appToast.error("此規格目前無法為您的帳戶下單，請改選其他規格");
      } else {
        appToast.error("加入購物車失敗，請稍後再試");
      }
      inFlightRef.current = false;
      setSubmitting(false);
    }
  };

  return { submitting, addToCart };
}
